import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Drawer,
  Image,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Upload,
  message,
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  SwapOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import ListPageLayout from '../../components/ListPage/ListPageLayout';
import api from '../../services/api';
import styles from './Mediatheque.module.css';

const STATUS_LABELS = {
  used: { label: 'Utilisé', color: 'green' },
  unused: { label: 'Inutilisé', color: 'default' },
  cropped_original: { label: 'Original croppé', color: 'orange' },
};

const formatBytes = (bytes) => {
  if (!bytes) return '0 o';
  const units = ['o', 'Ko', 'Mo', 'Go'];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const Mediatheque = () => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [reassignSource, setReassignSource] = useState(null);
  const [reassignTargetId, setReassignTargetId] = useState(null);
  const [replacing, setReplacing] = useState(false);
  const [reassigning, setReassigning] = useState(false);

  const fetchLibrary = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (search.trim()) params.append('search', search.trim());
      const response = await api.get(`/api/admin/media/library?${params.toString()}`);
      setStats(response.data?.stats || null);
      setItems(response.data?.items || []);
    } catch (error) {
      console.error(error);
      message.error('Erreur lors du chargement de la médiathèque');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => {
    fetchLibrary();
  }, [fetchLibrary]);

  const handleUpload = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post('/api/admin/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      message.success('Image uploadée');
      fetchLibrary();
    } catch (error) {
      message.error(error.response?.data?.error || 'Erreur upload');
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/admin/media/${id}`);
      message.success('Média supprimé');
      if (selectedItem?.id === id) {
        setDrawerOpen(false);
        setSelectedItem(null);
      }
      fetchLibrary();
    } catch (error) {
      message.error(error.response?.data?.error || 'Erreur suppression');
    }
  };

  const handleReplace = async (file) => {
    if (!replaceTarget) return false;
    setReplacing(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await api.post(`/api/admin/media/${replaceTarget.id}/replace`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      message.success(`${response.data?.updatedReferences ?? 0} référence(s) mise(s) à jour`);
      setReplaceTarget(null);
      fetchLibrary();
    } catch (error) {
      message.error(error.response?.data?.error || 'Erreur remplacement');
    } finally {
      setReplacing(false);
    }
    return false;
  };

  const handleReassign = async () => {
    if (!reassignSource || !reassignTargetId) return;
    setReassigning(true);
    try {
      const response = await api.post(`/api/admin/media/${reassignSource.id}/reassign`, {
        targetMediaId: reassignTargetId,
      });
      message.success(`${response.data?.updatedReferences ?? 0} référence(s) réassignée(s)`);
      setReassignSource(null);
      setReassignTargetId(null);
      fetchLibrary();
    } catch (error) {
      message.error(error.response?.data?.error || 'Erreur réassignation');
    } finally {
      setReassigning(false);
    }
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      message.success('URL copiée');
    } catch {
      message.error('Impossible de copier');
    }
  };

  const openDetail = async (item) => {
    try {
      const response = await api.get(`/api/admin/media/${item.id}/detail`);
      setSelectedItem(response.data);
      setDrawerOpen(true);
    } catch {
      setSelectedItem(item);
      setDrawerOpen(true);
    }
  };

  const reassignOptions = useMemo(
    () => items
      .filter((item) => !reassignSource || item.id !== reassignSource.id)
      .map((item) => ({
        value: item.id,
        label: `${item.originalFilename} (#${item.id})`,
      })),
    [items, reassignSource]
  );

  const columns = [
    {
      title: 'Aperçu',
      dataIndex: 'url',
      width: 90,
      render: (url, record) => (
        <Image
          src={url}
          alt={record.originalFilename}
          width={56}
          height={56}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          preview={{ mask: <EyeOutlined /> }}
        />
      ),
    },
    {
      title: 'Fichier',
      dataIndex: 'originalFilename',
      render: (name, record) => (
        <div>
          <div className={styles.fileName}>{name}</div>
          <div className={styles.fileMeta}>#{record.id} · {record.mimeType}</div>
        </div>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      width: 140,
      render: (status) => {
        const meta = STATUS_LABELS[status] || { label: status, color: 'default' };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: 'Références',
      dataIndex: 'referenceCount',
      width: 110,
      render: (count) => (count > 0 ? <Tag color="blue">{count}</Tag> : <span>0</span>),
    },
    {
      title: 'Taille',
      dataIndex: 'size',
      width: 90,
      render: (size) => formatBytes(size),
    },
    {
      title: 'Uploadé le',
      dataIndex: 'uploadedAt',
      width: 150,
      render: (date) => (date ? new Date(date).toLocaleString('fr-FR') : '—'),
    },
    {
      title: 'Par',
      dataIndex: 'uploadedBy',
      width: 120,
      render: (user) => user?.pseudo || '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <Space size="small" wrap>
          <Tooltip title="Détails">
            <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(record)} />
          </Tooltip>
          <Tooltip title="Copier l’URL">
            <Button size="small" icon={<CopyOutlined />} onClick={() => copyUrl(record.url)} />
          </Tooltip>
          <Tooltip title="Remplacer le fichier">
            <Button size="small" icon={<UploadOutlined />} onClick={() => setReplaceTarget(record)} />
          </Tooltip>
          <Tooltip title="Réassigner vers un autre média">
            <Button size="small" icon={<SwapOutlined />} onClick={() => setReassignSource(record)} />
          </Tooltip>
          <Popconfirm
            title={record.referenceCount > 0 ? 'Ce média est utilisé. Confirmer la suppression ?' : 'Supprimer ce média ?'}
            onConfirm={() => handleDelete(record.id)}
            okText="Supprimer"
            cancelText="Annuler"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <ListPageLayout
      breadcrumbs={[{ label: 'Médiathèque' }]}
      actions={[{ label: 'Actualiser', icon: <ReloadOutlined />, onClick: fetchLibrary, disabled: loading }]}
    >
      {stats && (
        <Row gutter={[16, 16]} className={styles.statsRow}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small"><Statistic title="Fichiers" value={stats.totalCount} /></Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small"><Statistic title="Volume total" value={formatBytes(stats.totalSizeBytes)} /></Card>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Card size="small"><Statistic title="Utilisés" value={stats.usedCount} valueStyle={{ color: '#3f8600' }} /></Card>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Card size="small"><Statistic title="Inutilisés" value={stats.unusedCount} /></Card>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Card size="small"><Statistic title="Originaux croppés" value={stats.croppedOriginalCount} valueStyle={{ color: '#d48806' }} /></Card>
          </Col>
        </Row>
      )}

      <Card className={styles.uploadCard} title="Uploader une image">
        <Upload.Dragger
          accept="image/*"
          showUploadList={false}
          beforeUpload={handleUpload}
          disabled={uploading}
          multiple={false}
        >
          <p className="ant-upload-drag-icon"><UploadOutlined /></p>
          <p className="ant-upload-text">Glissez une image ou cliquez pour uploader</p>
          <p className="ant-upload-hint">JPG, PNG, GIF, WebP, SVG — max 20 Mo</p>
        </Upload.Dragger>
      </Card>

      <Card className={styles.tableCard}>
        <Space wrap className={styles.filters}>
          <Input.Search
            placeholder="Rechercher un fichier..."
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={fetchLibrary}
            style={{ width: 280 }}
          />
          <Select
            allowClear
            placeholder="Filtrer par statut"
            style={{ width: 200 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={Object.entries(STATUS_LABELS).map(([value, meta]) => ({
              value,
              label: meta.label,
            }))}
          />
        </Space>

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={items}
          pagination={{ pageSize: 20, showSizeChanger: true }}
          scroll={{ x: 1100 }}
        />
      </Card>

      <Drawer
        title={selectedItem ? selectedItem.originalFilename : 'Détail'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={480}
      >
        {selectedItem && (
          <div className={styles.drawerContent}>
            <Image src={selectedItem.url} alt={selectedItem.originalFilename} style={{ maxWidth: '100%' }} />
            <div className={styles.drawerMeta}>
              <p><strong>ID :</strong> {selectedItem.id}</p>
              <p><strong>Statut :</strong> {STATUS_LABELS[selectedItem.status]?.label || selectedItem.status}</p>
              <p><strong>Taille :</strong> {formatBytes(selectedItem.size)}</p>
              <p><strong>Clé S3 :</strong> <code>{selectedItem.s3Key}</code></p>
              <p><strong>Uploadé :</strong> {selectedItem.uploadedAt}</p>
              {selectedItem.uploadedBy && (
                <p><strong>Par :</strong> {selectedItem.uploadedBy.pseudo}</p>
              )}
              {selectedItem.parentMediaId && (
                <p><strong>Original de :</strong> #{selectedItem.parentMediaId} {selectedItem.parentOriginalFilename}</p>
              )}
              {selectedItem.childrenCount > 0 && (
                <p><strong>Versions croppées :</strong> {selectedItem.childrenCount}</p>
              )}
            </div>
            <div>
              <h4>Références ({selectedItem.referenceCount})</h4>
              {selectedItem.references?.length > 0 ? (
                <ul className={styles.referenceList}>
                  {selectedItem.references.map((ref, index) => (
                    <li key={`${ref.source}-${ref.entityId}-${index}`}>
                      <Tag>{ref.source}</Tag> {ref.label}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.emptyRefs}>Aucune référence détectée sur le site.</p>
              )}
            </div>
            <Space>
              <Button onClick={() => copyUrl(selectedItem.url)} icon={<CopyOutlined />}>Copier l’URL</Button>
              <Button onClick={() => setReplaceTarget(selectedItem)} icon={<UploadOutlined />}>Remplacer</Button>
            </Space>
          </div>
        )}
      </Drawer>

      <Modal
        title={`Remplacer : ${replaceTarget?.originalFilename || ''}`}
        open={!!replaceTarget}
        onCancel={() => setReplaceTarget(null)}
        footer={null}
      >
        <p>Toutes les références vers cette image seront mises à jour automatiquement.</p>
        <Upload.Dragger
          accept="image/*"
          showUploadList={false}
          beforeUpload={handleReplace}
          disabled={replacing}
        >
          <p className="ant-upload-drag-icon"><UploadOutlined /></p>
          <p className="ant-upload-text">Choisir le nouveau fichier</p>
        </Upload.Dragger>
      </Modal>

      <Modal
        title={`Réassigner : ${reassignSource?.originalFilename || ''}`}
        open={!!reassignSource}
        onCancel={() => { setReassignSource(null); setReassignTargetId(null); }}
        onOk={handleReassign}
        okText="Réassigner"
        confirmLoading={reassigning}
        okButtonProps={{ disabled: !reassignTargetId }}
      >
        <p>
          Toutes les références de ce média pointeront vers l’image cible.
          Le média source sera supprimé (fichier S3 + entrée BDD).
        </p>
        <Select
          showSearch
          placeholder="Choisir le média cible"
          style={{ width: '100%' }}
          value={reassignTargetId}
          onChange={setReassignTargetId}
          options={reassignOptions}
          optionFilterProp="label"
        />
      </Modal>
    </ListPageLayout>
  );
};

export default Mediatheque;
